import React, { createContext, useContext, useState, useCallback } from 'react';
import zhCN from '../locales/zh-CN';
import enUS from '../locales/en-US';

export const messages: Record<string, Record<string, string>> = {
  zh: zhCN,
  en: enUS,
};

export type Locale = 'zh' | 'en';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const LocaleContext = createContext<LocaleContextType>({
  locale: 'zh',
  setLocale: () => {},
  t: (key: string) => key,
});

export const useLocale = () => useContext(LocaleContext);

const STORAGE_KEY = 'jdsuit_locale';

export const getSavedLocale = (): Locale => {
  if (typeof localStorage === 'undefined') return 'zh';
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === 'en' ? 'en' : 'zh';
};

export const t = (key: string): string => messages[getSavedLocale()]?.[key] || key;

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'en' ? 'en' : 'zh';
  });

  const setLocale = useCallback((l: Locale) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLocaleState(l);
  }, []);

  const t = useCallback(
    (key: string) => messages[locale]?.[key] || key,
    [locale]
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
};
